import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Package, AlertTriangle, Pencil, X, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, ITEM_CATEGORIES } from '../lib/constants';
import { readListState, persistListState, useListScroll } from '../lib/listState';

const emptyRow = () => ({ item_id: '', type: 'in', quantity: '', actual_quantity: '', reason: '' });

const WarehousePage = () => {
  const { user } = useAuth();
  const savedListState = useRef(readListState('warehouse')).current;
  const [stocks, setStocks] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', category: '', unit: '', price: 0, min_stock: 0, is_regular: false });
  const [bulkLocation, setBulkLocation] = useState('');
  const [bulkReference, setBulkReference] = useState('');
  const [bulkRows, setBulkRows] = useState([emptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [filterLocation, setFilterLocation] = useState(savedListState.filterLocation || 'all');
  const [filterCategory, setFilterCategory] = useState(savedListState.filterCategory || 'all');
  const [searchQuery, setSearchQuery] = useState(savedListState.searchQuery || '');
  const [showOpnameDialog, setShowOpnameDialog] = useState(false);
  const [opnameLocation, setOpnameLocation] = useState('');
  const [opnamePreview, setOpnamePreview] = useState([]);
  const [opnameSubmitting, setOpnameSubmitting] = useState(false);
  const opnameFileInputRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    persistListState('warehouse', { filterLocation, filterCategory, searchQuery });
  }, [filterLocation, filterCategory, searchQuery]);

  useListScroll('warehouse', !loading);

  const loadData = async () => {
    try {
      const [stocksRes, itemsRes, locationsRes] = await Promise.all([
        api.get('/warehouse/stock'),
        api.get('/items', { params: { include_all: true } }),
        api.get('/locations'),
      ]);
      setStocks(stocksRes.data);
      setItems(itemsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally { setLoading(false); }
  };

  const stockAtLocation = useMemo(() => {
    if (!bulkLocation) return {};
    const map = {};
    stocks.forEach((s) => {
      if (s.location_id === bulkLocation) map[s.item_id] = s.quantity;
    });
    return map;
  }, [stocks, bulkLocation]);

  const updateRow = (idx, patch) => {
    setBulkRows((prev) => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const addRow = () => setBulkRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx) => setBulkRows((prev) => prev.length === 1 ? [emptyRow()] : prev.filter((_, i) => i !== idx));

  const openUpdateDialog = () => {
    setBulkLocation('');
    setBulkReference('');
    setBulkRows([emptyRow()]);
    setShowDialog(true);
  };

  const handleBulkSubmit = async () => {
    if (!bulkLocation) { toast.error('Pilih lokasi dulu'); return; }
    const cleanRows = bulkRows.filter((r) => r.item_id);
    if (cleanRows.length === 0) { toast.error('Minimal 1 barang harus diisi'); return; }
    // Client-side pre-check for opname reason
    for (const r of cleanRows) {
      if (r.type === 'opname') {
        const actual = parseInt(r.actual_quantity);
        const current = stockAtLocation[r.item_id] || 0;
        if (Number.isNaN(actual) || actual < 0) { toast.error('Jumlah aktual opname tidak valid'); return; }
        if (actual !== current && !r.reason.trim()) {
          toast.error(`Opname "${items.find((i) => i.id === r.item_id)?.name || r.item_id}": selisih ${actual - current}. Isi keterangan.`);
          return;
        }
      } else {
        const qty = parseInt(r.quantity);
        if (Number.isNaN(qty) || qty <= 0) { toast.error('Jumlah harus > 0'); return; }
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        location_id: bulkLocation,
        reference: bulkReference.trim() || null,
        items: cleanRows.map((r) => ({
          item_id: r.item_id,
          type: r.type,
          quantity: r.type === 'opname' ? null : parseInt(r.quantity),
          actual_quantity: r.type === 'opname' ? parseInt(r.actual_quantity) : null,
          reason: r.reason.trim() || null,
        })),
      };
      const res = await api.post('/warehouse/stock/bulk', payload);
      toast.success(res.data.message || 'Stok berhasil diperbarui');
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal update stok');
    } finally {
      setSubmitting(false);
    }
  };

  const openOpnameDialog = () => {
    setOpnameLocation('');
    setOpnamePreview([]);
    if (opnameFileInputRef.current) opnameFileInputRef.current.value = '';
    setShowOpnameDialog(true);
  };

  const handleDownloadOpnameTemplate = () => {
    if (!opnameLocation) {
      toast.error('Pilih lokasi dulu sebelum download template');
      return;
    }
    const stockMap = {};
    stocks.forEach((s) => {
      if (s.location_id === opnameLocation) stockMap[s.item_id] = s.quantity;
    });
    const rows = items.map((it) => ({
      name: it.name,
      unit: it.unit,
      system_quantity: stockMap[it.id] || 0,
      actual_quantity: '',
      reason: '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 35 }, { wch: 10 }, { wch: 16 }, { wch: 16 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stok Opname');
    XLSX.writeFile(wb, `template_opname_${opnameLocation}.xlsx`);
    toast.success('Template diunduh. Isi kolom actual_quantity, dan reason jika ada selisih.');
  };

  const handleOpnameFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!opnameLocation) {
      toast.error('Pilih lokasi dulu sebelum upload file');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet);

        const stockMap = {};
        stocks.forEach((s) => {
          if (s.location_id === opnameLocation) stockMap[s.item_id] = s.quantity;
        });

        const parsed = [];
        const notFound = [];
        rows.forEach((r) => {
          const name = String(r.name || '').trim();
          if (!name || r.actual_quantity === undefined || r.actual_quantity === '') return;
          const item = items.find((it) => it.name.trim().toLowerCase() === name.toLowerCase());
          if (!item) {
            notFound.push(name);
            return;
          }
          const actual = parseInt(r.actual_quantity);
          if (Number.isNaN(actual) || actual < 0) return;
          const systemQty = stockMap[item.id] || 0;
          parsed.push({
            item_id: item.id,
            item_name: item.name,
            unit: item.unit,
            system_quantity: systemQty,
            actual_quantity: actual,
            delta: actual - systemQty,
            reason: String(r.reason || '').trim(),
          });
        });

        if (notFound.length > 0) {
          toast.error(`${notFound.length} barang tidak ditemukan di master (nama tidak cocok): ${notFound.slice(0, 3).join(', ')}${notFound.length > 3 ? '...' : ''}`);
        }
        if (parsed.length === 0) {
          toast.error('Tidak ada baris valid untuk diproses (isi kolom actual_quantity)');
          return;
        }

        setOpnamePreview(parsed);
        toast.success(`${parsed.length} baris siap diproses`);
      } catch (error) {
        toast.error('Format file tidak valid. Gunakan template XLSX.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleOpnameSubmit = async () => {
    if (!opnameLocation) { toast.error('Pilih lokasi dulu'); return; }
    if (opnamePreview.length === 0) { toast.error('Upload file dulu'); return; }

    const missingReason = opnamePreview.find((r) => r.delta !== 0 && !r.reason);
    if (missingReason) {
      toast.error(`"${missingReason.item_name}": selisih ${missingReason.delta > 0 ? '+' : ''}${missingReason.delta}, kolom reason wajib diisi di Excel`);
      return;
    }

    setOpnameSubmitting(true);
    try {
      const payload = {
        location_id: opnameLocation,
        reference: `OPNAME-EXCEL-${opnameLocation}`,
        items: opnamePreview.map((r) => ({
          item_id: r.item_id,
          type: 'opname',
          actual_quantity: r.actual_quantity,
          reason: r.reason || null,
        })),
      };
      const res = await api.post('/warehouse/stock/bulk', payload);
      toast.success(res.data.message || 'Stok opname berhasil diproses');
      setShowOpnameDialog(false);
      setOpnamePreview([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal proses stok opname');
    } finally {
      setOpnameSubmitting(false);
    }
  };


  const openEditDialog = (stock) => {
    const item = items.find((i) => i.id === stock.item_id);
    if (!item) return;
    setEditingItem(item);
    setEditForm({
      name: item.name, category: item.category, unit: item.unit,
      price: item.price, min_stock: item.min_stock,
      is_regular: !!item.is_regular,
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    try {
      await api.put(`/items/${editingItem.id}`, {
        name: editForm.name.trim() || undefined,
        category: editForm.category || undefined,
        unit: editForm.unit.trim() || undefined,
        price: parseFloat(editForm.price),
        min_stock: parseInt(editForm.min_stock),
      });
      toast.success(`"${editForm.name}" berhasil diperbarui`);
      setShowEditDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal update barang');
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return stocks.filter((s) => {
      if (filterLocation !== 'all' && s.location_id !== filterLocation) return false;
      if (filterCategory !== 'all' && s.category !== filterCategory) return false;
      if (q && !(`${s.item_name || ''} ${s.category || ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [stocks, filterLocation, filterCategory, searchQuery]);

  const lowStockCount = filtered.filter((s) => s.is_low_stock).length;
  const canManage = ['super_admin', 'admin_gudang', 'admin_maintenance'].includes(user?.role);
  const canEditItems = ['super_admin', 'admin_gudang'].includes(user?.role);

  return (
    <Layout>
      <div className="space-y-6" data-testid="warehouse-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Gudang</p>
            <h1 className="text-4xl font-medium tracking-tight">Inventory & Stok</h1>
            <p className="text-muted-foreground mt-1">Kelola stok barang maintenance di gudang</p>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
            <Button variant="outline" onClick={openOpnameDialog} data-testid="opname-excel-button">
              <Upload className="w-4 h-4 mr-2" /> Import Excel (Stok Opname)
            </Button>
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" onClick={openUpdateDialog} data-testid="update-stock-button">
                  <Plus className="w-4 h-4 mr-2" /> Update Stok
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Update Stok — Bulk per Lokasi</DialogTitle>
                  <p className="text-sm text-muted-foreground">
                    Input beberapa barang sekaligus. Bisa gabung tipe: Barang Masuk, Barang Keluar, atau Stok Opname.
                  </p>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Lokasi Gudang *</Label>
                      <Select value={bulkLocation} onValueChange={setBulkLocation}>
                        <SelectTrigger data-testid="bulk-location-select"><SelectValue placeholder="Pilih lokasi" /></SelectTrigger>
                        <SelectContent>
                          {locations.map((loc) => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Referensi (opsional, contoh: nomor tiket)</Label>
                      <Input placeholder="TCK-XXX / PO / dll" value={bulkReference}
                        onChange={(e) => setBulkReference(e.target.value)}
                        data-testid="bulk-reference-input" />
                    </div>
                  </div>

                  <div className="border border-border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary/50 border-b border-border">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-semibold">Barang</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold w-32">Aksi</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold w-24">Jumlah</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold">Keterangan</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {bulkRows.map((row, idx) => {
                          const current = bulkLocation && row.item_id ? (stockAtLocation[row.item_id] || 0) : null;
                          const item = items.find((i) => i.id === row.item_id);
                          const actualNum = parseInt(row.actual_quantity);
                          const delta = row.type === 'opname' && !Number.isNaN(actualNum) && current !== null ? actualNum - current : null;
                          return (
                            <tr key={idx} className="align-top" data-testid={`bulk-row-${idx}`}>
                              <td className="px-3 py-2">
                                <Select value={row.item_id} onValueChange={(v) => updateRow(idx, { item_id: v })}>
                                  <SelectTrigger className="h-9" data-testid={`bulk-item-select-${idx}`}>
                                    <SelectValue placeholder="Pilih barang" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name} ({it.unit})</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                {current !== null && (
                                  <p className="text-[11px] text-muted-foreground mt-1">
                                    Stok saat ini di {bulkLocation}: <strong>{current} {item?.unit}</strong>
                                  </p>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <Select value={row.type} onValueChange={(v) => updateRow(idx, { type: v })}>
                                  <SelectTrigger className="h-9" data-testid={`bulk-type-select-${idx}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="in">Barang Masuk</SelectItem>
                                    <SelectItem value="out">Barang Keluar</SelectItem>
                                    <SelectItem value="opname">Stok Opname</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-3 py-2">
                                {row.type === 'opname' ? (
                                  <div>
                                    <Input type="number" min="0" placeholder="aktual"
                                      value={row.actual_quantity}
                                      onChange={(e) => updateRow(idx, { actual_quantity: e.target.value })}
                                      className="h-9 text-right"
                                      data-testid={`bulk-actualqty-input-${idx}`} />
                                    {delta !== null && delta !== 0 && (
                                      <p className={`text-[11px] mt-1 font-medium text-right ${delta > 0 ? 'text-green-700' : 'text-red-700'}`}>
                                        Selisih: {delta > 0 ? '+' : ''}{delta}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <Input type="number" min="1" placeholder="qty"
                                    value={row.quantity}
                                    onChange={(e) => updateRow(idx, { quantity: e.target.value })}
                                    className="h-9 text-right"
                                    data-testid={`bulk-qty-input-${idx}`} />
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  placeholder={row.type === 'opname' ? 'Wajib jika ada selisih' : 'Opsional'}
                                  value={row.reason}
                                  onChange={(e) => updateRow(idx, { reason: e.target.value })}
                                  className="h-9"
                                  data-testid={`bulk-reason-input-${idx}`} />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <button type="button" onClick={() => removeRow(idx)}
                                  className="text-muted-foreground hover:text-destructive"
                                  data-testid={`bulk-remove-${idx}`}>
                                  <X className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="bulk-add-row-button">
                    <Plus className="w-3 h-3 mr-1" /> Tambah Baris
                  </Button>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDialog(false)}>Batal</Button>
                  <Button onClick={handleBulkSubmit} disabled={submitting}
                    className="bg-primary hover:bg-primary/90" data-testid="save-bulk-stock-button">
                    {submitting ? 'Menyimpan...' : `Simpan ${bulkRows.filter((r) => r.item_id).length} Baris`}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card className="border-border">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Cari</Label>
              <Input placeholder="Nama barang / kategori..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9" data-testid="stock-search-input" />
            </div>
            <div>
              <Label className="text-xs">Lokasi</Label>
              <Select value={filterLocation} onValueChange={setFilterLocation}>
                <SelectTrigger className="h-9" data-testid="stock-filter-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Lokasi</SelectItem>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Kategori</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-9" data-testid="stock-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Kategori</SelectItem>
                  {ITEM_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {lowStockCount > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0" />
              <p className="text-sm text-amber-900">
                <span className="font-semibold">{lowStockCount} barang</span> memiliki stok di bawah minimum
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-xl tracking-tight font-medium">Daftar Stok ({filtered.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Tidak ada stok yang cocok dengan filter</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 border-b border-border">
                    <tr>
                      <th className="text-left px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Barang</th>
                      <th className="text-left px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Kategori</th>
                      <th className="text-left px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Lokasi</th>
                      <th className="text-right px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Stok</th>
                      <th className="text-right px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Harga</th>
                      <th className="text-right px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                      <th className="text-right px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map((stock, idx) => (
                      <tr key={idx} className="hover:bg-secondary/30" data-testid={`stock-row-${idx}`}>
                        <td className="px-6 py-4 font-medium">{stock.item_name}</td>
                        <td className="px-6 py-4 text-muted-foreground capitalize">{stock.category}</td>
                        <td className="px-6 py-4 font-mono text-xs">{stock.location_id}</td>
                        <td className="px-6 py-4 text-right font-medium">{stock.quantity} {stock.unit}</td>
                        <td className="px-6 py-4 text-right">{formatCurrency(stock.price)}</td>
                        <td className="px-6 py-4 text-right">
                          {stock.is_low_stock ? (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">Stok Menipis</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Aman</Badge>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">
                          {canEditItems && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                              onClick={() => openEditDialog(stock)}
                              title="Edit barang"
                              data-testid={`edit-item-button-${idx}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Mass Stock Opname via Excel Dialog */}
        <Dialog open={showOpnameDialog} onOpenChange={setShowOpnameDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Stok Opname Massal — Import Excel</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Hitung fisik semua barang di satu lokasi sekaligus lewat Excel. Selisih otomatis tercatat di Arus Stok.
              </p>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-secondary/40 border border-border space-y-2">
                <p className="text-sm font-semibold">Langkah:</p>
                <ol className="text-sm space-y-1 list-decimal list-inside text-muted-foreground">
                  <li>Pilih lokasi di bawah</li>
                  <li>Download template — otomatis berisi semua barang &amp; stok sistem saat ini di lokasi itu</li>
                  <li>Isi kolom <span className="font-mono text-xs">actual_quantity</span> sesuai hitung fisik</li>
                  <li>Isi <span className="font-mono text-xs">reason</span> untuk barang yang selisih (wajib)</li>
                  <li>Upload file yang sudah diisi</li>
                </ol>
              </div>

              <div className="space-y-2">
                <Label>Lokasi *</Label>
                <Select
                  value={opnameLocation}
                  onValueChange={(v) => { setOpnameLocation(v); setOpnamePreview([]); if (opnameFileInputRef.current) opnameFileInputRef.current.value = ''; }}
                >
                  <SelectTrigger data-testid="opname-location-select"><SelectValue placeholder="Pilih lokasi" /></SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <Button type="button" variant="outline" onClick={handleDownloadOpnameTemplate} disabled={!opnameLocation} data-testid="download-opname-template-button">
                <Download className="w-4 h-4 mr-2" /> Download Template
              </Button>

              <div className="space-y-2">
                <Label>Upload File Excel</Label>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  ref={opnameFileInputRef}
                  onChange={handleOpnameFileChange}
                  disabled={!opnameLocation}
                  data-testid="opname-file-input"
                />
              </div>

              {opnamePreview.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-secondary/50 border-b border-border">
                    <p className="text-sm font-semibold">Preview Selisih</p>
                    <span className="text-xs text-muted-foreground">{opnamePreview.length} barang</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-semibold">Nama</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold">Sistem</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold">Aktual</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold">Selisih</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold">Keterangan</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {opnamePreview.map((r, idx) => (
                          <tr key={idx} className={r.delta !== 0 && !r.reason ? 'bg-red-50' : ''}>
                            <td className="px-3 py-2">{r.item_name}</td>
                            <td className="px-3 py-2 text-right text-muted-foreground">{r.system_quantity} {r.unit}</td>
                            <td className="px-3 py-2 text-right">{r.actual_quantity} {r.unit}</td>
                            <td className={`px-3 py-2 text-right font-medium ${r.delta > 0 ? 'text-green-700' : r.delta < 0 ? 'text-red-700' : 'text-muted-foreground'}`}>
                              {r.delta > 0 ? '+' : ''}{r.delta}
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {r.reason || (r.delta !== 0 ? <span className="text-red-600 font-medium">Wajib diisi</span> : '—')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowOpnameDialog(false)}>Batal</Button>
              <Button
                onClick={handleOpnameSubmit}
                disabled={opnameSubmitting || opnamePreview.length === 0}
                className="bg-primary hover:bg-primary/90"
                data-testid="save-opname-button"
              >
                {opnameSubmitting ? 'Memproses...' : `Proses ${opnamePreview.length} Barang`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Item Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Master Barang</DialogTitle></DialogHeader>
            {editingItem && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nama</Label>
                  <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    data-testid="edit-item-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Kategori</Label>
                    <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                      <SelectTrigger data-testid="edit-item-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ITEM_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Satuan</Label>
                    <Input value={editForm.unit} onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })}
                      data-testid="edit-item-unit-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Harga (Rp)</Label>
                    <Input type="number" min="0" value={editForm.price}
                      onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                      data-testid="edit-item-price-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Stok Minimum</Label>
                    <Input type="number" min="0" value={editForm.min_stock}
                      onChange={(e) => setEditForm({ ...editForm, min_stock: e.target.value })}
                      data-testid="edit-item-minstock-input" />
                    <p className="text-[11px] text-muted-foreground">Batas stok &quot;aman&quot;</p>
                  </div>
                </div>
                <label className="flex items-start gap-2 p-3 rounded-md border border-border bg-secondary/40 cursor-pointer" data-testid="edit-item-regular-label">
                  <input
                    type="checkbox"
                    checked={editForm.is_regular}
                    onChange={(e) => setEditForm({ ...editForm, is_regular: e.target.checked })}
                    className="mt-1"
                    data-testid="edit-item-regular-checkbox"
                  />
                  <div>
                    <p className="text-sm font-medium">Barang Regular</p>
                    <p className="text-xs text-muted-foreground">
                      Kalau dicentang, barang tetap tampil di master & inventori meski stok = 0.
                      Kalau tidak, barang otomatis disembunyikan saat stok mencapai 0 (riwayat tetap tersimpan).
                    </p>
                  </div>
                </label>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Batal</Button>
              <Button onClick={handleSaveEdit} className="bg-primary hover:bg-primary/90" data-testid="save-edit-item-button">
                Simpan Perubahan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default WarehousePage;
